import type { AssetId, TimeStreamAsset } from 'owa-timestream-types';
import { canSyncAssets } from '../service';
import { mutator } from 'satcheljs';
import { ObservableMap } from 'mobx';
import { setProject, setProjects } from '../privateActions';
import upsertProject from './projects/upsertProject';
import getProjectId from '../utils/getProjectId';
import getProjectIdInfo from '../utils/getProjectIdInfo';
import getStore from '../store';
import getTenantAndMailbox from '../utils/getTenantAndMailbox';
import markAllAssetsAsDeleted from './assets/markAllAssetsAsDeleted';
import markUnknownProjectsAsDeleted from './projects/markUnknownProjectsAsDeleted';
import updateProjectWithPatch from './projects/updateProjectWithPatch';
import {
    changeBackgroundColor,
    openTimeStreamProject,
    deleteTimeStreamProject,
    timeStreamProjectCreated,
    timeStreamProjectUpdated,
    timeStreamProjectPreviewOpened,
} from '../actions';
import getSelectedProjectId from '../selectors/getSelectedProjectId';
import updateTimeStreamProject from '../utils/updateTimeStreamProject';
import { trace } from 'owa-trace';

mutator(openTimeStreamProject, ({ projectId }) => {
    const { projects } = getStore();
    const project = projects.get(projectId);
    if (!project) {
        const { tenant, mailbox, outlookSpaceId, isShared } = getProjectIdInfo(projectId);
        projects.set(projectId, {
            tenant,
            mailbox,
            isShared,
            projectId,
            outlookSpaceId,
            outlookSpaceKey: null,
            name: '',
            description: '',
            categories: [],
            keywords: [],
            people: [],
            image: '',
            tasksParentFolderId: '',
            createdDateTime: '',
            lastModifiedDateTime: '',
            unseenCapturedCount: 0,
            templateType: '',
            appName: '',
            assets: new ObservableMap<AssetId, TimeStreamAsset>(),
            renderingOrder: [],
            newlyCreated: false,
            loaded: false,
            isLoading: false,
            loadingFailed: false,
            pendingLoad: false,
            deleted: false,
            isPreview: false,
            backgroundColor: '',
            canSync: canSyncAssets(projectId),
            skipToken: '',
            deltaToken: '',
        });
    } else {
        // Reset delta sync, so we ensure closing-reopening a project will start fresh.
        project.skipToken = '';
        project.deltaToken = '';

        // Cleanup assets we believe are deleted.
        const assets = project.assets;
        [...assets.entries()].forEach(([id, asset]) => {
            if (asset.deleted) {
                assets.delete(id);
            }
        });
    }
});

mutator(deleteTimeStreamProject, ({ projectId }) => {
    const project = getStore().projects.get(projectId);
    if (project) {
        project.deleted = true;
        markAllAssetsAsDeleted(project.assets);
    }
});

mutator(timeStreamProjectCreated, ({ targetMailbox, outlookSpace }) => {
    const { tenant, mailbox } = getTenantAndMailbox(targetMailbox);
    upsertProject(
        getStore().projects,
        getProjectId(tenant, mailbox, outlookSpace.id),
        outlookSpace,
        outlookSpace.assets,
        true
    );
});

mutator(timeStreamProjectPreviewOpened, ({ outlookSpace, assets }) => {
    upsertProject(
        getStore().projects,
        getProjectId('' /* tenant */, '' /* mailbox */, outlookSpace.id),
        outlookSpace,
        assets,
        false /* isNewlyCreated */,
        true /* isPreview */
    );
});

mutator(timeStreamProjectUpdated, ({ projectId, outlookSpace: patch }) => {
    const project = getStore().projects.get(projectId);
    if (project) {
        updateProjectWithPatch(project, patch);
    }
});

mutator(setProjects, ({ sourceMailbox, outlookSpaces, appName }) => {
    let appSpaces = outlookSpaces;

    const projects = getStore().projects;
    const { tenant, mailbox } = getTenantAndMailbox(sourceMailbox);
    appSpaces.forEach(outlookSpace =>
        upsertProject(projects, getProjectId(tenant, mailbox, outlookSpace.id), outlookSpace)
    );

    markUnknownProjectsAsDeleted(projects, tenant, mailbox, appSpaces);
});

mutator(setProject, ({ projectId, outlookSpace, outlookSpaceAssets }) => {
    upsertProject(getStore().projects, projectId, outlookSpace, outlookSpaceAssets);
});

mutator(changeBackgroundColor, ({ newColorHex, newColorText }) => {
    try {
        const projectUpdates = { backgroundColor: newColorHex };
        updateTimeStreamProject(getSelectedProjectId()!, projectUpdates);
    } catch (e) {
        trace.warn('changeBackgroundColorOfBoardFailure, error in updating server: ' + { e });
    }
});
